import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

import { Box, useColorModeValue } from "@chakra-ui/react";
import React, { useRef, useCallback } from "react";
import usePersistedState from "utilities/usePersistedState";
import Navigation from "./Navigation";
import Script from "./Script";
import Summary from "./Summary";

function Sidebar({ onVideoClick, isSidebarCollapsed, setIsSidebarCollapsed }) {
  const mainPanel = useRef();
  const [activeView, setActiveView] = usePersistedState(
    "sidebarActiveView",
    "summary"
  );

  const sidebarBg = useColorModeValue("white", "navy.800");

  const toggleCollapse = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, [setIsSidebarCollapsed]);

  const handleViewChange = useCallback(
    (viewId) => {
      if (viewId === activeView) {
        // If clicking the active view, toggle collapse
        setIsSidebarCollapsed((prev) => !prev);
      } else {
        // If selecting a new view, update view and ensure sidebar is expanded
        setActiveView(viewId);
        if (isSidebarCollapsed) {
          setIsSidebarCollapsed(false);
        }
      }
    },
    [activeView, isSidebarCollapsed, setActiveView, setIsSidebarCollapsed]
  );

  const renderViewContent = useCallback(() => {
    switch (activeView) {
      case "script":
        return <Script />;
      case "summary":
      default:
        return <Summary onVideoClick={onVideoClick} />;
    }
  }, [activeView, onVideoClick]);

  return (
    <Box
      display="flex"
      flexShrink={0}
      h="100%"
      id="sidebar"
      position="relative"
      ref={mainPanel}
      transition="all 0.2s ease"
      width={isSidebarCollapsed ? "57px" : "320px"}
    >
      <Navigation activeView={activeView} onViewChange={handleViewChange} />
      <Box
        bg={sidebarBg}
        borderRight={isSidebarCollapsed ? "none" : "1px solid"}
        borderRightColor={useColorModeValue("gray.200", "gray.700")}
        h="100%"
        position="relative"
        pt={2}
        transition="0.2s linear"
        w="100%"
      >
        <Box
          _hover={{
            bg: useColorModeValue("gray.100", "gray.700"),
            borderColor: useColorModeValue("gray.300", "gray.600"),
          }}
          alignItems="center"
          aria-label="Toggle sidebar"
          as="button"
          bg={useColorModeValue("white", "navy.800")}
          border="1px solid"
          borderColor={useColorModeValue("gray.200", "gray.700")}
          borderRadius="full"
          boxShadow="sm"
          cursor="pointer"
          display="flex"
          justifyContent="center"
          onClick={toggleCollapse}
          p={2}
          position="absolute"
          right={isSidebarCollapsed ? "-20px" : "-16px"}
          top="55px"
          transition="all 0.2s ease"
          zIndex={5}
        >
          {isSidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </Box>
        <Box
          display={isSidebarCollapsed ? "none" : undefined}
          h="100%"
          overflowY="auto"
          p={isSidebarCollapsed ? 0 : 4}
        >
          {renderViewContent()}
        </Box>
      </Box>
    </Box>
  );
}

export default Sidebar;
